import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Line, ReferenceArea, ReferenceLine } from 'recharts';
import { AppLayout } from './AppLayout';
import { ApiService } from '../services/apiService';
import { CivilLawsuitBucketHit } from '../types';
import { TrendResultsList } from './TrendResultsList';

type IntervalOption = 'day' | 'week' | 'month' | 'quarter' | 'year';
type SearchMode = 'vector' | 'text';

interface HistogramBucket {
  date: string;
  count: number;
  percent?: number;
}

interface ChunkInfo {
  from: string;
  to: string;
  hitCount: number;
}

interface FacetBucket {
  value: string;
  count: number;
}

/** Compute auto-interval from a date range span in months. */
const computeAutoInterval = (fromDate: string | null, toDate: string | null): IntervalOption => {
  if (!fromDate || !toDate) return 'month';
  const from = new Date(fromDate);
  const to = new Date(toDate);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return 'month';
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (months > 24) return 'month';
  if (months >= 1) return 'week';
  return 'day';
};

const formatBucketDate = (dateStr: string, interval: IntervalOption): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  switch (interval) {
    case 'day': return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'week': return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'month': return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    case 'quarter': return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
    case 'year': return d.getFullYear().toString();
    default: return dateStr;
  }
};

const DEBOUNCE_MS = 800;
const QUERY_DEBOUNCE_MS = 1200;
const STORAGE_KEY = 'trendSearch.settings';

/** Fills missing date buckets with zero counts between from/to for the given interval. Uses UTC arithmetic to avoid timezone drift. */
const zeroFillBuckets = (buckets: HistogramBucket[], from: string, to: string, interval: IntervalOption): HistogramBucket[] => {
  if (!from || !to) return buckets;
  const start = new Date(from + 'T00:00:00Z');
  const end = new Date(to + 'T00:00:00Z');
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return buckets;

  const bucketMap = new Map<string, HistogramBucket>();
  buckets.forEach(b => bucketMap.set(b.date.slice(0, 10), b));

  // Determine default percent for zero-filled entries (100 when no query active, undefined otherwise)
  const defaultPercent = buckets.length > 0 && buckets.every(b => b.percent === 100) ? 100 : undefined;

  const filled: HistogramBucket[] = [];
  const cur = new Date(start);

  // Align to interval start (all in UTC)
  if (interval === 'month') cur.setUTCDate(1);
  else if (interval === 'quarter') { cur.setUTCDate(1); cur.setUTCMonth(Math.floor(cur.getUTCMonth() / 3) * 3); }
  else if (interval === 'year') { cur.setUTCDate(1); cur.setUTCMonth(0); }
  else if (interval === 'week') { const day = cur.getUTCDay(); cur.setUTCDate(cur.getUTCDate() - (day === 0 ? 6 : day - 1)); }

  while (cur <= end) {
    const key = cur.toISOString().slice(0, 10);
    const existing = bucketMap.get(key);
    filled.push(existing ?? { date: key, count: 0, percent: defaultPercent });

    // Advance by interval (UTC)
    if (interval === 'day') cur.setUTCDate(cur.getUTCDate() + 1);
    else if (interval === 'week') cur.setUTCDate(cur.getUTCDate() + 7);
    else if (interval === 'month') cur.setUTCMonth(cur.getUTCMonth() + 1);
    else if (interval === 'quarter') cur.setUTCMonth(cur.getUTCMonth() + 3);
    else if (interval === 'year') cur.setUTCFullYear(cur.getUTCFullYear() + 1);
  }

  return filled;
};

interface TrendSettings {
  query: string;
  searchMode: SearchMode;
  selectedFields: { factsEmbedding: boolean; causeOfActionEmbedding: boolean; reliefEmbedding: boolean };
  courtStates: string[];
  courtName: string;
  defendants: string[];
  plaintiffLawyerFirms: string[];
  knnK: number;
  knnNumCandidates: number;
  maxChunks: number;
}

const getDefaults = (): TrendSettings => {
  return {
    query: '',
    searchMode: 'vector',
    selectedFields: { factsEmbedding: false, causeOfActionEmbedding: true, reliefEmbedding: false },
    courtStates: [],
    courtName: '',
    defendants: [],
    plaintiffLawyerFirms: [],
    knnK: 500,
    knnNumCandidates: 1000,
    maxChunks: 1
  };
};

const loadSettings = (): TrendSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...getDefaults(), ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return getDefaults();
};

const saveSettings = (settings: TrendSettings) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
};

export const TrendSearchPage = () => {
  const initial = loadSettings();

  const [query, setQuery] = useState(initial.query);
  const queryRef = useRef(initial.query);
  const queryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>(initial.searchMode);
  const [selectedFields, setSelectedFields] = useState(initial.selectedFields);
  const [selectedCourtStates, setSelectedCourtStates] = useState<string[]>(initial.courtStates);
  const [selectedCourtName, setSelectedCourtName] = useState(initial.courtName);
  const [selectedDefendants, setSelectedDefendants] = useState<string[]>(initial.defendants);
  const [selectedPlaintiffLawFirms, setSelectedPlaintiffLawFirms] = useState<string[]>(initial.plaintiffLawyerFirms);
  const [knnK, setKnnK] = useState(initial.knnK);
  const [knnNumCandidates, setKnnNumCandidates] = useState(initial.knnNumCandidates);
  const [maxChunks, setMaxChunks] = useState(initial.maxChunks);
  const [buckets, setBuckets] = useState<HistogramBucket[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [chunks, setChunks] = useState<ChunkInfo[]>([]);

  // Facet data
  const [facets, setFacets] = useState<{
    courtStates: FacetBucket[];
    defendants: FacetBucket[];
    plaintiffLawyerFirms: FacetBucket[];
    minFiledAt?: string;
    maxFiledAt?: string;
  }>({ courtStates: [], defendants: [], plaintiffLawyerFirms: [] });

  // Paginated full list state
  const [listHits, setListHits] = useState<CivilLawsuitBucketHit[]>([]);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [isListLoading, setIsListLoading] = useState(false);

  // Chart zoom state — stores the zoomed date range (triggers re-fetch)
  const [zoomFrom, setZoomFrom] = useState<string | null>(null);
  const [zoomTo, setZoomTo] = useState<string | null>(null);
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // Overview mini-chart: full (unzoomed) buckets + drag state
  const [overviewBuckets, setOverviewBuckets] = useState<HistogramBucket[]>([]);
  const [overviewRefLeft, setOverviewRefLeft] = useState<string | null>(null);
  const [overviewRefRight, setOverviewRefRight] = useState<string | null>(null);
  const [isOverviewSelecting, setIsOverviewSelecting] = useState(false);

  const apiService = ApiService.getInstance();
  const PAGE_SIZE = 100;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  // Derived: auto-interval from facets min/max or zoom range
  const interval = computeAutoInterval(zoomFrom ?? facets.minFiledAt?.slice(0, 10) ?? null, zoomTo ?? facets.maxFiledAt?.slice(0, 10) ?? null);
  const effectiveFrom = zoomFrom ?? facets.minFiledAt?.slice(0, 10) ?? null;
  const effectiveTo = zoomTo ?? facets.maxFiledAt?.slice(0, 10) ?? null;

  // Persist settings to localStorage
  useEffect(() => {
    saveSettings({ query, searchMode, selectedFields, courtStates: selectedCourtStates, courtName: selectedCourtName, defendants: selectedDefendants, plaintiffLawyerFirms: selectedPlaintiffLawFirms, knnK, knnNumCandidates, maxChunks });
  }, [query, searchMode, selectedFields, selectedCourtStates, selectedCourtName, selectedDefendants, selectedPlaintiffLawFirms, knnK, knnNumCandidates, maxChunks]);

  const resetAllSettings = () => {
    const defaults = getDefaults();
    setQuery(defaults.query);
    queryRef.current = defaults.query;
    setSearchMode(defaults.searchMode);
    setSelectedFields(defaults.selectedFields);
    setSelectedCourtStates([]);
    setSelectedCourtName('');
    setSelectedDefendants([]);
    setSelectedPlaintiffLawFirms([]);
    setKnnK(defaults.knnK);
    setKnnNumCandidates(defaults.knnNumCandidates);
    setMaxChunks(defaults.maxChunks);
    setZoomFrom(null);
    setZoomTo(null);
  };

  /** Enrich buckets with percent based on baseline. */
  const enrichBucketsWithPercent = (responseBuckets: HistogramBucket[], baselineBuckets: HistogramBucket[] | null, q: string): HistogramBucket[] => {
    if (!q.trim() || !baselineBuckets) {
      return responseBuckets.map(b => ({ ...b, percent: 100 }));
    }
    const baselineMap = new Map<string, number>();
    baselineBuckets.forEach(b => baselineMap.set(b.date, b.count));
    return responseBuckets.map(b => {
      const total = baselineMap.get(b.date) ?? 0;
      return { ...b, percent: total > 0 ? Math.round((b.count / total) * 1000) / 10 : 0 };
    });
  };

  const loadFacets = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const fields = Object.entries(selectedFields).filter(([_, v]) => v).map(([key]) => key);
    try {
      const result = await apiService.getCivilLawsuitsFacets({
        query: query || undefined,
        embeddingFields: fields,
        mode: searchMode,
        knnK,
        knnNumCandidates,
        maxChunks,
        courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
        courtName: selectedCourtName || undefined,
        defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
        plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
        filedAtFrom: zoomFrom ?? undefined,
        filedAtTo: zoomTo ?? undefined,
        signal: controller.signal
      });
      if (!controller.signal.aborted) setFacets(result);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Facets failed:', error);
    }
  }, [apiService, query, searchMode, selectedFields, selectedCourtStates, selectedCourtName, selectedDefendants, selectedPlaintiffLawFirms, knnK, knnNumCandidates, maxChunks, zoomFrom, zoomTo]);

  const handleSearch = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const fields = Object.entries(selectedFields).filter(([_, v]) => v).map(([key]) => key);
    const requestFilters = {
      courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
      courtName: selectedCourtName || undefined,
      defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
      plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
      filedAtFrom: facets.minFiledAt?.slice(0, 10) || undefined,
      filedAtTo: facets.maxFiledAt?.slice(0, 10) || undefined
    };

    setIsLoading(true);
    setHasSearched(true);
    try {
      const response = await apiService.getCivilLawsuitsHistogram({
        query: query || undefined,
        fields,
        filters: requestFilters,
        interval,
        mode: searchMode,
        knnK,
        knnNumCandidates,
        maxChunks,
        signal: controller.signal
      });

      let baselineBuckets: HistogramBucket[] | null = null;
      if (query.trim()) {
        const baseline = await apiService.getCivilLawsuitsHistogram({
          fields,
          filters: requestFilters,
          interval,
          mode: searchMode,
          knnK,
          knnNumCandidates,
          maxChunks,
          signal: controller.signal
        });
        baselineBuckets = baseline.buckets ?? [];
      }

      const responseBuckets = response.buckets ?? [];
      const enriched = enrichBucketsWithPercent(responseBuckets, baselineBuckets, query);
      const fromDate = facets.minFiledAt?.slice(0, 10) ?? '';
      const toDate = facets.maxFiledAt?.slice(0, 10) ?? '';
      const filledOverview = zeroFillBuckets(enriched, fromDate, toDate, interval);
      setOverviewBuckets(filledOverview);
      if (zoomFrom && zoomTo) {
        fetchZoomedHistogram(zoomFrom, zoomTo);
      } else {
        setBuckets(filledOverview);
        setTotalCount(response.totalCount ?? 0);
        setChunks(response.chunks ?? []);
      }
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Histogram failed:', error);
      setBuckets([]);
      setOverviewBuckets([]);
      setTotalCount(0);
      setChunks([]);
    } finally {
      if (!controller.signal.aborted && !(zoomFrom && zoomTo)) setIsLoading(false);
    }
  }, [apiService, query, interval, searchMode, selectedFields, selectedCourtStates, selectedCourtName, selectedDefendants, selectedPlaintiffLawFirms, knnK, knnNumCandidates, maxChunks, zoomFrom, zoomTo, facets.minFiledAt, facets.maxFiledAt]);

  /** Fetch histogram for a zoomed date range. */
  const fetchZoomedHistogram = async (from: string, to: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const fields = Object.entries(selectedFields).filter(([_, v]) => v).map(([key]) => key);
    const zoomedFilters = {
      courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
      courtName: selectedCourtName || undefined,
      defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
      plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
      filedAtFrom: from,
      filedAtTo: to
    };

    setIsLoading(true);
    try {
      const zoomInterval = computeAutoInterval(from, to);
      const response = await apiService.getCivilLawsuitsHistogram({
        query: query || undefined,
        fields,
        filters: zoomedFilters,
        interval: zoomInterval,
        mode: searchMode,
        knnK,
        knnNumCandidates,
        maxChunks,
        signal: controller.signal
      });

      let baselineBuckets: HistogramBucket[] | null = null;
      if (query.trim()) {
        const baseline = await apiService.getCivilLawsuitsHistogram({
          fields,
          filters: zoomedFilters,
          interval: zoomInterval,
          mode: searchMode,
          knnK,
          knnNumCandidates,
          maxChunks,
          signal: controller.signal
        });
        baselineBuckets = baseline.buckets ?? [];
      }

      const responseBuckets = response.buckets ?? [];
      setBuckets(zeroFillBuckets(enrichBucketsWithPercent(responseBuckets, baselineBuckets, query), from, to, zoomInterval));
      setTotalCount(response.totalCount ?? 0);
      setChunks(response.chunks ?? []);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Zoomed histogram failed:', error);
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  };

  const loadList = useCallback((page: number) => {
    const fields = Object.entries(selectedFields).filter(([_, v]) => v).map(([key]) => key);
    const listFilters = {
      courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
      courtName: selectedCourtName || undefined,
      defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
      plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
      filedAtFrom: zoomFrom ?? (facets.minFiledAt?.slice(0, 10) || undefined),
      filedAtTo: zoomTo ?? (facets.maxFiledAt?.slice(0, 10) || undefined)
    };

    setIsListLoading(true);
    apiService.getCivilLawsuitsList({
      query: query || undefined,
      fields,
      filters: listFilters,
      mode: searchMode,
      knnK,
      knnNumCandidates,
      maxChunks,
      page,
      pageSize: PAGE_SIZE
    }).then(result => {
      setListHits(result.hits);
      setListTotalCount(result.totalCount);
      setListPage(page);
    }).catch(error => {
      console.error('List load failed:', error);
      setListHits([]);
      setListTotalCount(0);
    }).finally(() => {
      setIsListLoading(false);
    });
  }, [apiService, query, searchMode, selectedFields, selectedCourtStates, selectedCourtName, selectedDefendants, selectedPlaintiffLawFirms, knnK, knnNumCandidates, maxChunks, zoomFrom, zoomTo, facets.minFiledAt, facets.maxFiledAt]);

  const handleListPageChange = (newPage: number) => {
    loadList(newPage);
  };

  // Trigger search + facets on mount
  useEffect(() => {
    loadFacets();
    handleSearch();
    loadList(1);
  }, []);

  // Trigger debounced search when any filter/param changes
  const fieldsDep = JSON.stringify(selectedFields);
  const courtStatesDep = JSON.stringify(selectedCourtStates);
  const defendantsDep = JSON.stringify(selectedDefendants);
  const firmsDep = JSON.stringify(selectedPlaintiffLawFirms);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setIsSearchPending(true);
    debounceRef.current = setTimeout(() => {
      setIsSearchPending(false);
      loadFacets();
      handleSearch();
      loadList(1);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, searchMode, fieldsDep, courtStatesDep, selectedCourtName, defendantsDep, firmsDep, knnK, knnNumCandidates, maxChunks]);

  // When zoom changes, re-fetch histogram + list for the zoomed range
  useEffect(() => {
    if (!hasSearched) return;
    if (zoomFrom && zoomTo) {
      fetchZoomedHistogram(zoomFrom, zoomTo);
    }
    loadList(1);
  }, [zoomFrom, zoomTo]);

  // Chart zoom is handled by the built-in Brush component + drag-to-select on chart

  const handleMouseDown = (e: any) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (!isSelecting || !refAreaLeft || !refAreaRight) {
      setIsSelecting(false);
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    // Find bucket dates corresponding to the selected labels
    let leftIdx = chartData.findIndex(d => d.label === refAreaLeft);
    let rightIdx = chartData.findIndex(d => d.label === refAreaRight);
    if (leftIdx > rightIdx) [leftIdx, rightIdx] = [rightIdx, leftIdx];
    if (leftIdx === rightIdx || leftIdx < 0 || rightIdx < 0) {
      setIsSelecting(false);
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    const fromDate = chartData[leftIdx].date;
    const toDate = chartData[rightIdx].date;
    setZoomFrom(fromDate);
    setZoomTo(toDate);
    setIsSelecting(false);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const resetZoom = () => {
    setZoomFrom(null);
    setZoomTo(null);
    // Restore overview buckets as main chart
    setBuckets(overviewBuckets);
  };

  // Overview mini-chart drag handlers (operates on overviewBuckets)
  const handleOverviewMouseDown = (e: any) => {
    if (e && e.activeLabel) {
      setOverviewRefLeft(e.activeLabel);
      setIsOverviewSelecting(true);
    }
  };

  const handleOverviewMouseMove = (e: any) => {
    if (isOverviewSelecting && e && e.activeLabel) {
      setOverviewRefRight(e.activeLabel);
    }
  };

  const handleOverviewMouseUp = () => {
    if (!isOverviewSelecting || !overviewRefLeft || !overviewRefRight) {
      setIsOverviewSelecting(false);
      setOverviewRefLeft(null);
      setOverviewRefRight(null);
      return;
    }
    let leftIdx = overviewChartData.findIndex(d => d.label === overviewRefLeft);
    let rightIdx = overviewChartData.findIndex(d => d.label === overviewRefRight);
    if (leftIdx > rightIdx) [leftIdx, rightIdx] = [rightIdx, leftIdx];
    if (leftIdx === rightIdx || leftIdx < 0 || rightIdx < 0) {
      setIsOverviewSelecting(false);
      setOverviewRefLeft(null);
      setOverviewRefRight(null);
      return;
    }
    const fromDate = overviewChartData[leftIdx].date;
    const toDate = overviewChartData[rightIdx].date;
    setZoomFrom(fromDate);
    setZoomTo(toDate);
    setIsOverviewSelecting(false);
    setOverviewRefLeft(null);
    setOverviewRefRight(null);
  };

  // Prepare chart data — main chart uses current buckets, overview uses full unzoomed set
  const chartData = useMemo(() => buckets.map(b => ({
    label: formatBucketDate(b.date, interval),
    count: b.count,
    percent: b.percent ?? 100,
    date: b.date
  })), [buckets, interval]);

  const overviewChartData = useMemo(() => overviewBuckets.map(b => ({
    label: formatBucketDate(b.date, interval),
    count: b.count,
    percent: b.percent ?? 100,
    date: b.date
  })), [overviewBuckets, interval]);

  const isZoomed = zoomFrom !== null && zoomTo !== null;

  const showPercentLine = useMemo(() => buckets.some(b => b.percent !== undefined && b.percent !== null && b.percent !== 100), [buckets]);

  // Compute chunk boundary labels for chart reference lines
  const chunkBoundaryLabels = useMemo(() => chunks.length > 1
    ? chunks.slice(1).map(c => formatBucketDate(c.from, interval))
    : [], [chunks, interval]);

  const toggleField = (field: keyof typeof selectedFields) => {
    setSelectedFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <AppLayout>
      <main data-component="TrendSearchPage" className="flex-1 overflow-auto">
        <div className="flex h-full">
          {/* Left Sidebar — Filters */}
          <aside className="w-72 flex-shrink-0 border-r border-gray-200 bg-gray-50 p-4 overflow-y-auto">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Filters</h2>

            <FacetMultiSelect
              label="Court State"
              options={facets.courtStates}
              selected={selectedCourtStates}
              onChange={setSelectedCourtStates}
            />

            <FilterInput label="Court" value={selectedCourtName} placeholder="e.g., District Court"
              onChange={setSelectedCourtName} />

            <FacetMultiSelect
              label="Defendant"
              options={facets.defendants}
              selected={selectedDefendants}
              onChange={setSelectedDefendants}
            />

            <FacetMultiSelect
              label="Plaintiff Law Firm"
              options={facets.plaintiffLawyerFirms}
              selected={selectedPlaintiffLawFirms}
              onChange={setSelectedPlaintiffLawFirms}
            />

            {(effectiveFrom || effectiveTo) && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-400">
                  Range: {effectiveFrom ?? '?'} — {effectiveTo ?? '?'}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Interval: {interval} (auto)
                </div>
              </div>
            )}

            <div className={`mt-6 border-t border-gray-200 pt-4 ${searchMode !== 'vector' ? 'opacity-40 pointer-events-none' : ''}`}>
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Semantic Search Parameters</h3>
              <p className="text-[10px] text-gray-400 mb-3">Only applies in Semantic mode</p>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">k (max results per field)</label>
                <input type="number" value={knnK} min={1} max={10000} step={100}
                  onChange={e => {
                    const val = Math.max(1, Math.min(10000, parseInt(e.target.value) || 2000));
                    setKnnK(val);
                    if (val > knnNumCandidates) setKnnNumCandidates(val);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">numCandidates (pool size)</label>
                <input type="number" value={knnNumCandidates} min={1} max={10000} step={500}
                  onChange={e => {
                    const val = Math.max(1, Math.min(10000, parseInt(e.target.value) || 10000));
                    setKnnNumCandidates(val);
                    if (val < knnK) setKnnK(val);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Max Chunks</label>
                <input type="number" value={maxChunks} min={1} max={24} step={1}
                  onChange={e => setMaxChunks(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                <p className="mt-1 text-[10px] text-gray-400">Splits kNN search into N time windows for better recall. 1 = no chunking.</p>
              </div>
              {knnK > knnNumCandidates && (
                <p className="mt-1 text-[10px] text-red-500">k must be ≤ numCandidates</p>
              )}
              <p className="mt-2 text-[10px] text-gray-400">k ≤ numCandidates ≤ 10,000. Higher k = more results but slower.</p>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">Civil Lawsuit Trends</h1>

            {/* Unified Search Box with embedded field toggles */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  defaultValue={query}
                  key={query === '' ? 'reset' : 'active'}
                  onChange={e => {
                    queryRef.current = e.target.value;
                    if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
                    queryDebounceRef.current = setTimeout(() => {
                      setQuery(queryRef.current);
                    }, QUERY_DEBOUNCE_MS);
                  }}
                  placeholder="Search query (optional)..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex items-center gap-3 border-l border-gray-200 pl-3">
                  <FieldToggle label="Facts" checked={selectedFields.factsEmbedding} color="blue"
                    onChange={() => toggleField('factsEmbedding')} />
                  <FieldToggle label="Cause" checked={selectedFields.causeOfActionEmbedding} color="purple"
                    onChange={() => toggleField('causeOfActionEmbedding')} />
                  <FieldToggle label="Relief" checked={selectedFields.reliefEmbedding} color="green"
                    onChange={() => toggleField('reliefEmbedding')} />
                </div>
                <div className="flex items-center border-l border-gray-200 pl-3">
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    <button onClick={() => setSearchMode('vector')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${searchMode === 'vector' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      Semantic
                    </button>
                    <button onClick={() => setSearchMode('text')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${searchMode === 'text' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      Phrase
                    </button>
                  </div>
                </div>
                <button onClick={resetAllSettings}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors">
                  Reset
                </button>
              </div>
            </div>

            {/* Histogram Chart */}
            {hasSearched && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isLoading ? 'Loading...' : (
                      <>
                        Filing Trends ({totalCount.toLocaleString()} total)
                        {isZoomed && (
                          <span className="text-sm font-normal text-blue-600 ml-2">
                            — viewing {listTotalCount.toLocaleString()} in selection
                          </span>
                        )}
                        {isSearchPending && (
                          <span className="text-sm font-normal text-gray-400 ml-2">updating…</span>
                        )}
                      </>
                    )}
                  </h3>
                  <div className="flex items-center gap-3">
                    {isZoomed && (
                      <button onClick={resetZoom}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                        Reset Zoom
                      </button>
                    )}
                    {!isLoading && chartData.length > 0 && (
                      <span className="text-xs text-gray-400">Drag on chart to zoom</span>
                    )}
                  </div>
                </div>
                {!isLoading && chartData.length > 0 ? (<div className={isSearchPending ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                      {showPercentLine && (
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={40} domain={[0, 100]} unit="%" />
                      )}
                      <Tooltip
                        wrapperStyle={{ zIndex: 10 }}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', padding: '8px 12px', backgroundColor: '#fff' }}
                        formatter={(value: any, name: any) => {
                          if (name === 'percent') return [`${value}%`, '% of all cases'];
                          return [Number(value).toLocaleString(), 'Filings'];
                        }}
                      />
                      <Line yAxisId="left" type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
                      {showPercentLine && (
                        <Line yAxisId="right" type="monotone" dataKey="percent" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
                      )}
                      {refAreaLeft && refAreaRight && (
                        <ReferenceArea yAxisId="left" x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} fill="#3b82f6" fillOpacity={0.15} />
                      )}
                      {chunkBoundaryLabels.map((label, i) => (
                        <ReferenceLine key={`chunk-${i}`} yAxisId="left" x={label} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5} strokeOpacity={0.6} />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                  {chunks.length > 1 && (
                    <div className="flex gap-1 mt-2 ml-[50px] mr-[50px]">
                      {chunks.map((c, i) => (
                        <div key={i} className="flex-1 text-center px-1 py-0.5 bg-red-50 border border-red-200 rounded text-[10px] text-red-700 leading-tight">
                          <span className="font-medium">Chunk {i + 1}</span>
                          <br />
                          {c.hitCount.toLocaleString()} hits
                        </div>
                      ))}
                    </div>
                  )}
                  {overviewChartData.length > 0 && (
                    <div className={`mt-2 relative ml-[50px] ${showPercentLine ? 'mr-[50px]' : 'mr-[30px]'}`}>
                      <ResponsiveContainer width="100%" height={40}>
                        <ComposedChart
                          data={overviewChartData}
                          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                          onMouseDown={handleOverviewMouseDown}
                          onMouseMove={handleOverviewMouseMove}
                          onMouseUp={handleOverviewMouseUp}
                        >
                          <Line type="monotone" dataKey="count" stroke="#cbd5e1" strokeWidth={1} dot={false} cursor="crosshair" />
                          {isZoomed && zoomFrom && zoomTo && (
                            <ReferenceArea
                              x1={formatBucketDate(zoomFrom, interval)}
                              x2={formatBucketDate(zoomTo, interval)}
                              fill="#3b82f6"
                              fillOpacity={0.2}
                              stroke="#3b82f6"
                              strokeOpacity={0.5}
                            />
                          )}
                          {overviewRefLeft && overviewRefRight && (
                            <ReferenceArea
                              x1={overviewRefLeft}
                              x2={overviewRefRight}
                              fill="#2563eb"
                              fillOpacity={0.25}
                              stroke="#2563eb"
                              strokeOpacity={0.6}
                            />
                          )}
                          <XAxis dataKey="label" hide />
                          <YAxis hide />
                        </ComposedChart>
                      </ResponsiveContainer>
                      <div className="absolute top-0 right-0 text-[10px] text-gray-400">
                        {isZoomed ? 'drag to re-select range' : 'drag to zoom'}
                      </div>
                    </div>
                  )}
                </div>) : !isLoading ? (
                  <div className="text-center text-gray-500 py-12">
                    No data found for the selected filters and date range.
                  </div>
                ) : null}
              </div>
            )}

            {/* Full Results List */}
            {hasSearched && (
              <TrendResultsList
                hits={listHits}
                totalCount={listTotalCount}
                page={listPage}
                pageSize={PAGE_SIZE}
                isLoading={isListLoading}
                isZoomed={isZoomed}
                onPageChange={handleListPageChange}
              />
            )}
          </div>
        </div>
      </main>
    </AppLayout>
  );
};

/* ── Small helper components ── */

const FilterInput = ({ label, value, placeholder, onChange }: {
  label: string; value: string; placeholder: string; onChange: (v: string) => void;
}) => (
  <div className="mt-3 first:mt-0">
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
  </div>
);

const FieldToggle = ({ label, checked, color, onChange }: {
  label: string; checked: boolean; color: string; onChange: () => void;
}) => {
  const ring = color === 'blue' ? 'focus:ring-blue-500' : color === 'purple' ? 'focus:ring-purple-500' : 'focus:ring-green-500';
  const accent = color === 'blue' ? 'text-blue-600' : color === 'purple' ? 'text-purple-600' : 'text-green-600';
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={onChange}
        className={`w-3.5 h-3.5 ${accent} border-gray-300 rounded ${ring}`} />
      <span className="text-xs text-gray-600">{label}</span>
    </label>
  );
};

const FacetMultiSelect = ({ label, options, selected, onChange }: {
  label: string;
  options: FacetBucket[];
  selected: string[];
  onChange: (v: string[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = options.filter(o => o.value.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  return (
    <div ref={ref} data-component="FacetMultiSelect" className="mt-3 first:mt-0 relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-2 py-1.5 border border-gray-300 rounded text-sm bg-white hover:border-gray-400"
      >
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <span className="flex items-center gap-1">
          {selected.length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{selected.length}</span>
          )}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No results</div>
            ) : (
              filtered.map(opt => (
                <label key={opt.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="flex-1 text-xs text-gray-700 truncate">{opt.value}</span>
                  <span className="text-xs text-gray-400">({opt.count.toLocaleString()})</span>
                </label>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => onChange([])}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
